/**
 * Shared deletion guard utilities for parlays.
 * Used by web and mobile to enforce consistent deletion rules.
 */

import { isValidBetStatus } from './betResult';

// Minimal interfaces — consumers pass their own typed objects,
// we only require the fields needed for guard checks.

interface DeletableLeg {
  origin?: string | null;
  hit?: boolean | null;
  dnp?: boolean | null;
  gameDate?: Date | string | null;
  gameStartsAt?: Date | string | null;
  settledAt?: Date | string | null;
}

interface DeletableParlay {
  result?: string | null;
  settledAt?: Date | string | null;
  legs?: DeletableLeg[];
}

// ---------------------------------------------------------------------------
// Parlay guards
// ---------------------------------------------------------------------------

/**
 * Returns a human-readable reason why a parlay cannot be deleted,
 * or null if deletion is allowed.
 */
export function getParlayDeletionBlockReason(parlay: DeletableParlay): string | null {
  const result = parlay.result;

  if (isValidBetStatus(result) && result !== 'pending') {
    return 'Cannot delete settled parlays';
  }

  if (parlay.settledAt != null) {
    return 'Cannot delete settled parlays';
  }

  // Block if any leg has been individually settled
  if (parlay.legs) {
    for (const leg of parlay.legs) {
      if (leg.hit !== null && leg.hit !== undefined) {
        return 'Cannot delete parlays with settled legs';
      }
    }
  }

  return null;
}

/**
 * Quick boolean check for whether a parlay can be deleted.
 */
export function canDeleteParlay(parlay: DeletableParlay): boolean {
  return getParlayDeletionBlockReason(parlay) === null;
}

/**
 * True when a refused delete means the caller's list is out of date (the parlay or one of its
 * legs settled, or the parlay is gone), so the caller should reload it.
 */
export function isStaleParlayDeleteRefusal(status: number): boolean {
  return status === 403 || status === 404;
}
